import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { Path, UseFormReturn, useWatch } from 'react-hook-form';

import { BatchBillingServiceContractsSettings } from '@work4all/models/lib/Classes/BatchBillingServiceContractsSettings.entity';
import { InputBatchBillingServiceContractsSettings } from '@work4all/models/lib/Classes/InputBatchBillingServiceContractsSettings.entity';

import { useUpsertBatchBillingServiceContractsSettingsMutation } from './use-upsert-batch-billing-service-contracts-settings';

type UseWatchedMutationOptions<BatchBillingServiceContractsSettings> = {
  form: UseFormReturn<BatchBillingServiceContractsSettings>;
  defaultValues: Partial<InputBatchBillingServiceContractsSettings>;
};

export function useWatchBillingServiceContractsSettingsUpdate<
  BatchBillingServiceContractsSettings
>({
  form,
  defaultValues,
}: UseWatchedMutationOptions<BatchBillingServiceContractsSettings>) {
  const [mutateBillingServiceContractsSettings] =
    useUpsertBatchBillingServiceContractsSettingsMutation();

  const batchBillingSettingsKeys = Object.keys(
    new BatchBillingServiceContractsSettings()
  ).filter((key) => key !== '__typename');

  const settingsValues = useWatch({
    control: form.control,
    name: batchBillingSettingsKeys as Path<BatchBillingServiceContractsSettings>[],
  });

  const settingsObject = useMemo(
    () =>
      batchBillingSettingsKeys.reduce((acc, key, index) => {
        acc[key] = settingsValues?.[index];
        return acc;
      }, {} as Partial<BatchBillingServiceContractsSettings>),
    [batchBillingSettingsKeys, settingsValues]
  );

  const prevValues = useRef(settingsObject);

  const triggerDebounced = useRef(
    debounce((vals: Partial<InputBatchBillingServiceContractsSettings>) => {
      const settings = batchBillingSettingsKeys.reduce((acc, key) => {
        const value = vals?.[key] === '' ? null : vals?.[key];
        acc[key] =
          value !== undefined
            ? value
            : defaultValues?.[key as keyof typeof defaultValues];
        return acc;
      }, {} as typeof defaultValues);

      mutateBillingServiceContractsSettings({
        variables: {
          settings,
        },
      });
    }, 500)
  ).current;

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      if (Object.keys(settingsObject).length > 0) {
        isFirstRender.current = false;
        prevValues.current = settingsObject;
      }
      return;
    }

    const hasChanged = !isEqual(prevValues.current, settingsObject);

    if (hasChanged) {
      prevValues.current = settingsObject;
      triggerDebounced(settingsObject);
    }
  }, [settingsObject, triggerDebounced]);
}
